<template>
	<div
		v-if="visible"
		data-test-id="mobile-app-banner"
		class="mobile-app-banner"
		:class="`mobile-app-banner--${variant}`"
		role="region"
		:aria-label="regionLabel">
		<div class="mobile-app-banner-icon" aria-hidden="true">
			<svg viewBox="0 0 67 67" width="28" height="28" focusable="false">
				<path
					fill="#ffffff"
					fill-rule="evenodd"
					clip-rule="evenodd"
					d="M35.0424 45.4211L37.7215 38.7938C60.0708 38.7938 65.5675 21.1514 65.0765 12.9194C64.5855 4.6874 57.7442 -0.335099 50.9055 0.0174166C47.3098 0.228925 39.484 2.485 34.5488 15.8805C33.3119 19.2274 31.9249 23.0249 30.8331 26.0136C29.8083 28.8193 29.0451 30.909 28.9086 31.2501C12.5682 24.6999 15.648 8.86174 16.1462 6.29974C14.6671 6.08064 13.116 6.22163 11.706 6.71515C0.355037 9.88776 0.355036 29.699 26.9346 36.8198L25.454 40.8384C6.20676 33.8587 0.70755 42.5305 0.0730267 46.4081C-0.702502 51.7663 4.7967 56.49 11.9175 57.9001C22.3519 59.9446 29.1906 54.2339 31.6582 51.4138C31.6582 51.4138 39.4135 57.6885 49.9889 64.8798C56.4752 69.3215 65.993 65.7963 66.698 57.9001C58.5347 59.234 49.4022 53.7842 41.8064 49.2515C39.3653 47.7948 37.0828 46.4328 35.0424 45.4211ZM47.5213 12.0733C48.6494 9.04173 50.7645 4.75713 53.7961 5.37562C56.8277 5.9941 56.9006 9.12831 56.8277 11.7913C56.542 22.2097 49.0724 33.4356 39.6955 33.4356C39.6955 33.4356 46.2523 15.387 47.5213 12.0733ZM9.09739 48.7347C8.74487 46.0556 11.706 39.9219 23.4094 46.1966C20.4483 54.9389 9.80241 54.2339 9.09739 48.7347Z" />
			</svg>
		</div>
		<div class="mobile-app-banner-text">
			<p class="mobile-app-banner-title">LiveSwitch App</p>
			<p class="mobile-app-banner-subtitle">{{ subtitle }}</p>
		</div>
		<a
			data-test-id="mobile-app-banner-action"
			class="mobile-app-banner-action"
			:href="href"
			:data-label="label"
			:rel="linkRel"
			@click="onActionClick">
			{{ label }}
		</a>
		<button
			data-test-id="mobile-app-banner-dismiss"
			type="button"
			class="mobile-app-banner-dismiss"
			aria-label="Dismiss"
			@click="onDismiss">
			<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
				<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
			</svg>
		</button>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, ref, watch } from "vue";

	import {
		detectMobileAppPlatform,
		isMobileAppBannerEligibleAccount,
		isMobileAppBannerEligibleBrowser,
		queryMobileAppInstallState,
		resolveMobileAppBannerHref,
		resolveMobileAppBannerLabel,
		type MobileAppBannerAccount,
		type MobileAppBannerLabel,
		type MobileAppBannerTelemetry,
		type MobileAppInstallState
	} from "../utilities/mobileAppBanner";

	/**
	 * LSP-21826: presentational Open/Get banner shared by contact-admin and contact-async. It decides
	 * visibility (phone or tablet browser, account not snoozed or opted out) and the OPEN/GET label, and emits
	 * what the user did. The host owns the API call, telemetry and folding the server's answer back
	 * onto the account (applyMobileAppBannerServerFields), because each app has its own API client and
	 * its own copy of the Datadog SDK.
	 */
	const props = withDefaults(
		defineProps<{
			/** The signed-in user's current-org account from /me. Null hides the banner, except in record mode. */
			account: MobileAppBannerAccount | null | undefined;
			/** "bar" is a full-width strip in page flow (admin); "card" floats in a fixed slot (recorder). */
			variant?: "bar" | "card";
			/** Test seam; defaults to the real window. */
			targetWindow?: Window;
			/**
			 * LSP-23306: record mode, for an agent who opened the recording from an integration. When set (see
			 * resolveMobileAppRecordHref), OPEN goes here, and there is no account gate and no install probe:
			 * the link itself hands off to the app or falls back to a web page.
			 */
			recordHref?: string | null;
			/** LSP-23507: record mode's subtitle, so a live call can say "Join this call…" instead of "Record this…". */
			recordSubtitle?: string;
		}>(),
		{ variant: "bar", targetWindow: undefined, recordHref: null, recordSubtitle: "Record this in the LiveSwitch app" }
	);

	const emit = defineEmits<{
		visible: [boolean];
		shown: [MobileAppBannerTelemetry];
		tapped: [MobileAppBannerTelemetry];
		dismissed: [MobileAppBannerTelemetry];
	}>();

	const hostWindow = props.targetWindow ?? (typeof window === "undefined" ? undefined : window);
	const platform = detectMobileAppPlatform(hostWindow?.navigator?.userAgent, hostWindow?.navigator);
	const eligibleBrowser = isMobileAppBannerEligibleBrowser(hostWindow);
	const installState = ref<MobileAppInstallState>("unknown");
	// AI review (PR 6410): stay hidden until the installed-app answer is in, so the first paint is
	// already the right label. Before this, Android painted GET (Play) and flipped to OPEN, and a quick
	// tap went to the store. queryMobileAppInstallState caps the wait.
	const installStateSettled = ref(false);
	// Flipped the instant the user dismisses, so the banner leaves before the PUT round-trips.
	const hiddenByUser = ref(false);

	const recordMode = computed(() => Boolean(props.recordHref));
	const label = computed<MobileAppBannerLabel>(() => (recordMode.value ? "Open" : resolveMobileAppBannerLabel(installState.value)));
	const href = computed(
		() => props.recordHref || resolveMobileAppBannerHref(platform, installState.value, props.account?.organizationId)
	);
	const eligibleAccount = computed(() => isMobileAppBannerEligibleAccount(props.account));
	const visible = computed(
		() => eligibleBrowser && !hiddenByUser.value && (recordMode.value || (installStateSettled.value && eligibleAccount.value))
	);
	const subtitle = computed(() => {
		if (recordMode.value) return props.recordSubtitle;
		return installState.value === "installed" ? "Open the LiveSwitch App" : "Now Available - Get the LiveSwitch App.";
	});
	const regionLabel = computed(() => (recordMode.value ? "Open the LiveSwitch app" : "Get the LiveSwitch app"));
	// Record mode must send the referrer: when the app does not open, the same tab lands on contact-admin's
	// /mobile/record page, which offers "Back to the recorder" only when the referrer is the async origin
	// (MobileRecordFallback.vue cameFromAsyncRecorder). The browser default sends only the origin cross-site.
	const linkRel = computed(() => (recordMode.value ? "noopener" : "noopener noreferrer"));

	function telemetry(): MobileAppBannerTelemetry {
		return { label: label.value, platform, installState: installState.value };
	}

	onMounted(async () => {
		// Record mode never asks Chrome: its link works whether or not the app is installed.
		if (!eligibleBrowser || recordMode.value) return;
		installState.value = await queryMobileAppInstallState(platform, hostWindow?.navigator);
		installStateSettled.value = true;
	});

	watch(visible, (value) => emit("visible", value), { immediate: true });

	// One `shown` per mount, with the label the user actually saw, so the install funnel counts views
	// and not label changes.
	let shownEmitted = false;
	watch(
		visible,
		(isVisible) => {
			if (!isVisible || shownEmitted) return;
			shownEmitted = true;
			emit("shown", telemetry());
		},
		{ immediate: true }
	);

	function onActionClick() {
		emit("tapped", telemetry());
	}

	function onDismiss() {
		const payload = telemetry();
		hiddenByUser.value = true;
		emit("dismissed", payload);
	}
</script>

<style scoped>
	.mobile-app-banner {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 10px 12px 10px 14px;
		background: #ffffff;
		color: rgba(0, 0, 0, 0.87);
		font-family: Inter, system-ui, sans-serif;
	}

	.mobile-app-banner--bar {
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	}

	.mobile-app-banner--card {
		border-radius: 14px;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
	}

	.mobile-app-banner-icon {
		width: 44px;
		height: 44px;
		border-radius: 11px;
		background: rgb(var(--v-theme-primary));
		display: grid;
		place-items: center;
		flex-shrink: 0;
	}

	.mobile-app-banner-text {
		flex: 1;
		min-width: 0;
	}

	.mobile-app-banner-text p {
		margin: 0;
		line-height: 1.25;
	}

	.mobile-app-banner-title {
		font-family: "Inter Tight", Inter, sans-serif;
		font-weight: 700;
		font-size: 16px;
	}

	.mobile-app-banner-subtitle {
		font-size: 0.75rem;
		color: rgba(0, 0, 0, 0.6);
		margin-top: 2px;
		white-space: normal;
		overflow-wrap: anywhere;
	}

	.mobile-app-banner-action {
		flex-shrink: 0;
		background: rgb(var(--v-theme-primary));
		color: #ffffff;
		font-family: "Inter Tight", Inter, sans-serif;
		font-weight: 700;
		font-size: 14px;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		text-decoration: none;
		padding: 9px 18px;
		border-radius: 999px;
		line-height: 1;
	}

	.mobile-app-banner-action:focus-visible,
	.mobile-app-banner-dismiss:focus-visible {
		outline: 2px solid rgb(var(--v-theme-primary));
		outline-offset: 2px;
	}

	.mobile-app-banner-dismiss {
		flex-shrink: 0;
		background: none;
		border: 0;
		padding: 4px;
		color: rgba(0, 0, 0, 0.45);
		display: grid;
		place-items: center;
		cursor: pointer;
	}
</style>
